import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/webpack.mjs";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export default function PdfPageReader({ src, title }) {
  const hostRef = useRef(null);
  const canvasRefs = useRef([]);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [targetWidth, setTargetWidth] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const node = hostRef.current;
    if (!node) return undefined;

    const measure = () => {
      const width = Math.floor(node.clientWidth || 0);
      setTargetWidth(width ? clamp(width, 320, 980) : 0);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    setError("");
    setPdfDoc(null);
    setNumPages(0);

    if (!src) return undefined;

    const task = pdfjsLib.getDocument(src);
    task.promise
      .then((doc) => {
        if (!active) {
          doc.destroy();
          return;
        }
        setPdfDoc(doc);
        setNumPages(doc.numPages);
      })
      .catch(() => {
        if (active) setError("The case study pages could not be rendered.");
      });

    return () => {
      active = false;
      task.destroy();
    };
  }, [src]);

  useEffect(() => {
    if (!pdfDoc || !numPages || !targetWidth) return undefined;

    let cancelled = false;

    const renderPages = async () => {
      for (let pageNumber = 1; pageNumber <= numPages; pageNumber += 1) {
        if (cancelled) return;
        const canvas = canvasRefs.current[pageNumber - 1];
        if (!canvas) continue;

        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const scale = targetWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });
        const outputScale = clamp(window.devicePixelRatio || 1, 1, 2);
        const context = canvas.getContext("2d", { alpha: false });

        canvas.width = Math.floor(scaledViewport.width * outputScale);
        canvas.height = Math.floor(scaledViewport.height * outputScale);
        canvas.style.width = `${Math.floor(scaledViewport.width)}px`;
        canvas.style.height = `${Math.floor(scaledViewport.height)}px`;

        if (!context) continue;
        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
        context.imageSmoothingEnabled = true;

        await page.render({
          canvasContext: context,
          viewport: scaledViewport
        }).promise;
      }
    };

    renderPages().catch(() => {
      if (!cancelled) setError("The case study pages could not be rendered.");
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, numPages, targetWidth]);

  return (
    <div ref={hostRef} className="w-full">
      {error ? (
        <div className="border border-[#E5E7EB] bg-white px-6 py-10 text-center text-sm text-[#6B7280]">
          {error}
        </div>
      ) : (
        <div className="pdf-page-stack">
          {Array.from({ length: numPages }, (_, index) => (
            <div key={`${title || "case-study"}-page-${index + 1}`} className="pdf-page-sheet">
              <canvas
                ref={(node) => {
                  canvasRefs.current[index] = node;
                }}
                className="pdf-page-canvas"
                aria-label={`${title || "Case study"} page ${index + 1}`}
              />
            </div>
          ))}
          {!numPages && (
            <div className="border border-[#E5E7EB] bg-white px-6 py-10 text-center text-sm text-[#6B7280]">
              Loading case study pages...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
